import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { Form, Input, InputNumber, message, Modal, Select } from 'antd';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { cashRegisterApi } from 'services/api/cashRegister.api';
import {
  CashRegisterOperationType,
  ICashRegister,
  ICashRegisterOperationDescription,
} from 'services/store/types/cashRegister/CashRegister';

const schema = yup.object({
  value: yup
    .number()
    .typeError('Wartość musi być liczbą')
    .required('Wartość jest wymagana')
    .min(1, 'Wartość musi być większa od 0'),
  description: yup
    .string()
    .required('Opis jest wymagany')
    .min(3, 'Min. 3 znaki')
    .max(250, 'Max. 250 znaków'),
});

interface IFormValues {
  value: number;
  description: string;
  additionalInfo: string;
}

interface IProps {
  cashRegister: ICashRegister;
  operationDescriptions: ICashRegisterOperationDescription[];
  closeModal: () => void;
  refetchCashRegister: () => Promise<any>;
}

const PayOutMoneyModal: React.FC<IProps> = ({
  closeModal,
  refetchCashRegister,
  cashRegister,
  operationDescriptions,
}) => {
  const { mutate: createCashRegisterOperation, isLoading } = useMutation({
    mutationKey: ['create-cash-register-operation'],
    mutationFn: (values: IFormValues) =>
      cashRegisterApi.createCashRegisterOperation({
        ...values,
        operationType: CashRegisterOperationType.OUT,
      }),
    onSuccess: async (res) => {
      await refetchCashRegister();
      message.success('Wypłacono pieniądze z kasy');
      closeModal();
    },
    onError: () => {
      message.error('Błąd!');
    },
  });

  const checkIsEnoughMoney = async (values: IFormValues) => {
    const result = cashRegister.value - formik.values.value;
    if (result < 0)
      return message.error('W kasie nie ma wystarczającej ilości pieniędzy');
    return createCashRegisterOperation(values);
  };

  const formik = useFormik<IFormValues>({
    initialValues: { value: 0, description: '', additionalInfo: '' },
    onSubmit: (values) => checkIsEnoughMoney(values),
    validationSchema: schema,
  });

  return (
    <Modal
      title="Wypłata z kasy"
      destroyOnClose
      visible
      onCancel={closeModal}
      maskClosable={false}
      onOk={() => formik.handleSubmit()}
      okButtonProps={{ loading: isLoading, disabled: !formik.isValid }}
      cancelButtonProps={{ disabled: isLoading }}
    >
      <Form labelCol={{ xs: { span: 24 }, sm: { span: 4 } }}>
        <Form.Item
          validateStatus={formik.errors.value && 'error'}
          help={formik.errors.value}
          label="Wartość"
          required
        >
          <InputNumber
            {...formik.getFieldProps('value')}
            onChange={(value) => formik.setFieldValue('value', value)}
            min={0}
            max={cashRegister.value}
          />
        </Form.Item>

        <Form.Item
          validateStatus={formik.errors.description && 'error'}
          help={formik.errors.description}
          label="Opis"
          required
        >
          <Select
            size="middle"
            {...formik.getFieldProps('description')}
            onChange={(value) => formik.setFieldValue('description', value)}
            options={operationDescriptions.map((desc) => ({
              label: desc.value,
              value: desc.value,
            }))}
          />
        </Form.Item>

        <Form.Item
          validateStatus={formik.errors.additionalInfo && 'error'}
          help={formik.errors.additionalInfo}
          label="Info"
        >
          <Input
            {...formik.getFieldProps('additionalInfo')}
            placeholder="Dodatkowe informacje"
          />
        </Form.Item>
        <div>
          <div>Aktualna wartość kasy: {cashRegister.value} zł</div>
          <div>
            Wartość kasy po wypłacie: {cashRegister.value - formik.values.value}{' '}
            zł
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default PayOutMoneyModal;
